import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SharedFromBadge } from '@/components/studies/SharedFromBadge';
import { SpeciesIcon } from '@/components/ui/species-icon';
import { formatPatientAge } from '@/lib/patientAge';
import { cn } from '@/lib/utils';
import { History, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
/**
 * Live-ingest affordance for a worklist row: this study's counts are still
 * climbing, so a modality is mid-archive and more images are on the way.
 * Deliberately the SAME idiom and wording as the viewer's SeriesStrip chip
 * (pulsing dot + "Receiving…") so the two surfaces agree — a radiologist who
 * learns it in one place reads it in the other.
 *
 * LAYOUT: sized to fit INSIDE the patient-cell metadata line, whose height is
 * pinned independently of its contents (see the `h-4 leading-4` container).
 * `shrink-0` keeps the chip whole; the species/age text beside it is the
 * flexible one. So the chip appearing or disappearing cannot move any element
 * or change the row height.
 *
 * COLOR: app-ui ships a single dark theme (tokens live in index.css `@theme`,
 * and nothing ever toggles a `.dark` class), so Tailwind `dark:` variants here
 * would track the OS scheme rather than the app surface — the trap that
 * produced washed-out modals in admin-ui. The chip instead carries its own
 * translucent tint + ring, so it reads against every background a row can
 * have (default, hover, expanded, and the red STAT wash) and stays legible if
 * a light surface ever lands underneath it.
 *
 * A11Y: not colour-only — there is a text label, a ring, and a title. The
 * chip is a polite live region, which is safe here precisely BECAUSE the
 * tracker's output is referentially stable: the node is created once when the
 * study starts arriving and then survives every subsequent poll untouched, so
 * this announces once per study, not once per poll. `aria-label` carries the
 * patient name so the announcement has context out of row order.
 */
function ReceivingChip({ studyId, patientName }) {
    const { t } = useTranslation();
    const description = t('worklist.receivingStudy', { name: patientName });
    return (_jsxs("span", { "data-testid": `worklist-receiving-${studyId}`, role: "status", "aria-label": description, title: description, className: "flex shrink-0 items-center gap-1 rounded bg-emerald-500/15 px-1 py-px text-[9px] leading-none font-semibold text-emerald-300 normal-case ring-1 ring-emerald-500/30 ring-inset", children: [_jsx("span", { "aria-hidden": true, className: "h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-current" }), t('worklist.receiving')] }));
}
/**
 * Patient name plus its inline markers (prior studies, key images, STAT). The
 * table truncates the name to its column (desktop hover shows the title; the
 * expanded card repeats it in full). The phone card list passes `wrap`: a
 * title tooltip never shows on touch, so there the name wraps to two lines.
 */
export function PatientNameLine({ study, name, isStat, onOpenKeyImages, wrap = false, }) {
    const { t } = useTranslation();
    return (_jsxs("div", { className: cn('flex gap-1.5', wrap ? 'items-start' : 'items-center'), children: [_jsx("span", { title: wrap ? undefined : name, className: cn('text-foreground text-sm font-medium', wrap ? 'line-clamp-2 min-w-0 break-words' : 'truncate'), "data-testid": `study-patient-name-${study.id}`, children: name }), (study.priorStudyCount ?? 0) > 0 && (
            // Neutral metadata ("3 priors"), not an alarm: amber is reserved for
            // pending / urgent / SLA-at-risk on this surface.
            _jsxs("span", { className: "text-muted-foreground flex shrink-0 items-center gap-0.5 text-[11px] font-semibold", title: t('worklist.priorStudies', {
                    count: study.priorStudyCount ?? 0,
                }), "aria-label": t('worklist.priorStudies', {
                    count: study.priorStudyCount ?? 0,
                }), "data-testid": `study-prior-studies-${study.id}`, children: [_jsx(History, { className: "h-3.5 w-3.5", "aria-hidden": true }), study.priorStudyCount] })), (study.bookmarkCount ?? 0) > 0 && (_jsxs("button", { type: "button", onClick: (e) => {
                    e.stopPropagation();
                    onOpenKeyImages();
                }, 
                // The glyph is ~14px tall; the pseudo-element takes the hit area to
                // 24px+ without moving anything.
                className: "text-primary hover:text-primary/80 relative flex shrink-0 items-center gap-0.5 text-[11px] font-semibold after:absolute after:-inset-x-1 after:-inset-y-1.5", title: t('worklist.keyImagesHint', {
                    count: study.bookmarkCount ?? 0,
                }), "aria-label": t('worklist.keyImages', {
                    count: study.bookmarkCount ?? 0,
                }), children: [_jsx(Star, { className: "h-3.5 w-3.5 fill-current", "aria-hidden": true }), study.bookmarkCount] })), isStat && (_jsx("span", { className: "shrink-0 animate-pulse rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white uppercase", children: "STAT" }))] }));
}
/**
 * Species/age line, and the home of the live-ingest chip. `h-4 leading-4` pins
 * this line's box independently of what is in it, and the chip is the LAST,
 * shrink-0 item — so the chip coming and going can neither change the row
 * height nor move anything beside it. The species text is the flexible one
 * (min-w-0 + truncate), so it yields instead of the row reflowing.
 */
export function PatientMetaLine({ study, name, isReceiving, className, }) {
    const { t } = useTranslation();
    return (_jsxs("div", { className: cn('text-muted-foreground/60 flex h-4 items-center gap-1 truncate text-[11px] leading-4 uppercase', className), children: [study.patient.species && (_jsx(SpeciesIcon, { species: study.patient.species, className: "h-3 w-3 shrink-0", "aria-hidden": true })), _jsx("span", { className: "min-w-0 truncate", children: [study.patient.species, formatPatientAge(study.patient.age, t)]
                    .filter(Boolean)
                    .join(' • ') || ' ' }), _jsx(SharedFromBadge, { accessVia: study.accessVia, testId: `worklist-shared-from-${study.id}` }), isReceiving && _jsx(ReceivingChip, { studyId: study.id, patientName: name })] }));
}
